import { createSlice } from "@reduxjs/toolkit";
import allFieldsFilled from "helpers/getIsAllFieldsFilled";
import getParsedLocalStorageData from "../helpers/getParsedLocalStorageData";
import config from "@Config/index";

interface leadFormModal {
  openLeadFormModal: boolean;
  inventory: Record<any, any>;
  personalDetails: Record<any, string>;
  accommodationDetails: Record<any, any>;
  isLeadFormFilled: string;
  isLFOneFilled: boolean;
  submittingFormFirstTime: boolean;
  leadFormModalHeading: string;
  leadFormPage: number;
  isLoading: boolean;
  inModal: boolean;
  openingSource: string;
  showError: boolean;
  activeTenancyId: number;
  showGenericForm: boolean;
  region?: any;
  intake?: string;
  isLeadSubmitted: boolean;
  isEditingLead: boolean;
  referDetails: any;
  hasSubmittedCommPreference?: boolean;
}

export const initialState: leadFormModal = {
  openLeadFormModal: false,
  inventory: {},
  personalDetails: {},
  accommodationDetails: {},
  leadFormModalHeading: "Enquire Now",
  isLeadFormFilled: "false",
  isLFOneFilled: false,
  submittingFormFirstTime: false,
  leadFormPage: 1,
  isLoading: false,
  inModal: false,
  openingSource: "",
  showError: false,
  activeTenancyId: 0,
  showGenericForm: true,
  region: {},
  intake: "",
  isLeadSubmitted: false,
  isEditingLead: false,
  referDetails: {},
  hasSubmittedCommPreference: false,
};

const getPhoneCode = (personalDetailsFromLocalStorage: any) => {
  const phoneCode = personalDetailsFromLocalStorage["user_details/phone_code"];
  const phoneCodeArr = (phoneCode || "").split(" ");
  return phoneCodeArr.length >= 2 ? phoneCodeArr.pop() : phoneCode;
};

const getLocalstorageData = () => {
  let personalDetailsFromLocalStorage: any = getParsedLocalStorageData("lead-form-one-saved-data");
  if (personalDetailsFromLocalStorage && Object.keys(personalDetailsFromLocalStorage).length > 0) {
    const partnerData =
      personalDetailsFromLocalStorage["region"] && personalDetailsFromLocalStorage["intake"]
        ? {
            region: personalDetailsFromLocalStorage["region"],
            intake: personalDetailsFromLocalStorage["intake"]
              ? personalDetailsFromLocalStorage["intake"]
              : null,
          }
        : {};
    const regionData =
      Object.keys(partnerData).length === 0 && personalDetailsFromLocalStorage["region"]
        ? { region: personalDetailsFromLocalStorage["region"] }
        : {};
    personalDetailsFromLocalStorage = {
      fullName: personalDetailsFromLocalStorage["user_details/name"],
      email: personalDetailsFromLocalStorage["user_details/email"],
      code: getPhoneCode(personalDetailsFromLocalStorage),
      mobileNumber: personalDetailsFromLocalStorage["user_details/phone"],
      preferredCommunicationModes: personalDetailsFromLocalStorage["preferred_communication_modes"],
      university: personalDetailsFromLocalStorage["university"],
      ...regionData,
      ...partnerData,
    };
  }
  let accommodationDetailsFromLocalStorage: any = getParsedLocalStorageData(
    "lead-form-two-saved-data",
  );

  if (
    accommodationDetailsFromLocalStorage &&
    Object.keys(accommodationDetailsFromLocalStorage).length > 0
  ) {
    accommodationDetailsFromLocalStorage = {
      people: accommodationDetailsFromLocalStorage["no_of_students"],
      selectedUniversity: accommodationDetailsFromLocalStorage["university"],
      selectedRoom: accommodationDetailsFromLocalStorage["configuration"],
      moveIn: accommodationDetailsFromLocalStorage["move_in_date"],
      selectedDuration: accommodationDetailsFromLocalStorage["lease_duration"],
      budget: accommodationDetailsFromLocalStorage["budget"],
    };
  }

  const isLeadFormSubmitted = localStorage.getItem("lead-form-submitted");
  const hasSubmittedCommPreference =
    localStorage.getItem("has-submitted-comm-preference") === "true";
  return {
    personalDetailsFromLocalStorage,
    accommodationDetailsFromLocalStorage,
    isLeadFormSubmitted,
    hasSubmittedCommPreference,
  };
};

const leadFormModal = createSlice({
  name: "leadFormModal",
  initialState,
  reducers: {
    setLocalStorageData: (state) => {
      try {
        const {
          personalDetailsFromLocalStorage,
          accommodationDetailsFromLocalStorage,
          isLeadFormSubmitted,
          hasSubmittedCommPreference,
        } = getLocalstorageData();

        if (
          personalDetailsFromLocalStorage &&
          Object.keys(personalDetailsFromLocalStorage).length > 0
        ) {
          const partnerDataFromLocalStorage =
            personalDetailsFromLocalStorage?.region && personalDetailsFromLocalStorage?.intake
              ? {
                  region: personalDetailsFromLocalStorage?.region,
                  intake: personalDetailsFromLocalStorage?.intake,
                }
              : {};
          const regionDataFromLocalStorage =
            Object.keys(partnerDataFromLocalStorage).length === 0 &&
            personalDetailsFromLocalStorage?.region
              ? { region: personalDetailsFromLocalStorage.region }
              : {};
          const personalDetailsToBeSaved: any = {
            fullName: personalDetailsFromLocalStorage?.fullName,
            ...(config.IS_CHINA_APP ? {} : { email: personalDetailsFromLocalStorage?.email }),
            code: personalDetailsFromLocalStorage?.code,
            mobileNumber: personalDetailsFromLocalStorage?.mobileNumber,
            ...(hasSubmittedCommPreference
              ? {
                  preferredCommunicationModes:
                    personalDetailsFromLocalStorage?.preferredCommunicationModes,
                }
              : {}),
            ...(personalDetailsFromLocalStorage?.university
              ? {
                  university: personalDetailsFromLocalStorage?.university,
                }
              : {}),
            ...regionDataFromLocalStorage,
            ...partnerDataFromLocalStorage,
          };

          state.personalDetails = personalDetailsToBeSaved;
          state.isLFOneFilled = allFieldsFilled(personalDetailsToBeSaved);
        }
        if (accommodationDetailsFromLocalStorage)
          state.accommodationDetails = accommodationDetailsFromLocalStorage;
        if (isLeadFormSubmitted) {
          state.isLeadFormFilled = isLeadFormSubmitted;
          if (isLeadFormSubmitted === "true") {
            state.leadFormPage = 2;
          }
        }
        if (hasSubmittedCommPreference) {
          state.hasSubmittedCommPreference = hasSubmittedCommPreference;
        }
      } catch (error) {
        console.error(error);
      }
    },
    openLeadFormModal: (state, { payload }) => {
      state.openLeadFormModal = true;
      state.inventory = payload.inventory;
      state.inModal = payload.inModal;
      state.leadFormModalHeading = payload.leadFormModalHeading || state.leadFormModalHeading;
      state.openingSource = payload.openingSource || state.openingSource;
      state.leadFormPage = payload.leadFormPage || 1;
      if (payload.referDetails) {
        state.referDetails = payload.referDetails;
      }
    },
    closeLeadFormModal: (state) => {
      state.openLeadFormModal = false;
      state.inventory = {};
      state.isLoading = false;
      state.inModal = false;
      state.leadFormModalHeading = initialState.leadFormModalHeading;
      state.leadFormPage = 1;
    },
    updateLeadFormState: (state, { payload }) => {
      state.leadFormPage = payload.leadFormPage;
      state.isLoading = false;
      state.submittingFormFirstTime = payload.submittingFormFirstTime;
      if (payload.leadFormPage === 2) {
        state.isEditingLead = false;
      }
    },
    updateIsEditingLead: (state, { payload }) => {
      state.isEditingLead = payload.isEditingLead;
    },
    updateSubmittingFormFirstTime: (state, { payload }) => {
      state.submittingFormFirstTime = payload.submittingFormFirstTime;
    },
    updateLeadFormPage: (state, { payload }) => {
      state.leadFormPage = payload.leadFormPage;
    },
    toggleLoading: (state, { payload }) => {
      state.isLoading = payload.isLoading;
    },
    updateInventory: (state, { payload }) => {
      state.inventory = payload.inventory;
    },
    updateOpeningSource: (state, { payload }) => {
      state.openingSource = payload.openingSource;
    },
    updateIsLFOneFilled: (state, { payload }) => {
      state.isLFOneFilled = payload.isLFOneFilled;
    },
    toggleErrorState: (state, { payload }) => {
      state.showError = payload.showError;
    },
    updateActiveTenancyId: (state, { payload }) => {
      state.activeTenancyId = payload.activeTenancyId;
    },
    updateShowGenericForm: (state, { payload }) => {
      state.showGenericForm = payload.showGenericForm;
    },
    isLeadSubmitted: (state, { payload }) => {
      state.isLeadSubmitted = payload.isLeadSubmitted;
    },
    updateHasSubmittedCommPreference: (state, { payload }) => {
      state.hasSubmittedCommPreference = payload.hasSubmittedCommPreference;
    },
    resetCommPreferenceFlag: (state) => {
      state.hasSubmittedCommPreference = false;
      localStorage.removeItem("has-submitted-comm-preference");
    },
    resetLeadSubmitted: (state) => {
      state.isLeadSubmitted = false;
    },
  },
});

export default leadFormModal.reducer;
export const {
  setLocalStorageData,
  openLeadFormModal,
  closeLeadFormModal,
  updateIsEditingLead,
  updateLeadFormState,
  toggleLoading,
  updateInventory,
  updateOpeningSource,
  updateIsLFOneFilled,
  toggleErrorState,
  updateActiveTenancyId,
  updateLeadFormPage,
  updateShowGenericForm,
  updateSubmittingFormFirstTime,
  isLeadSubmitted,
  updateHasSubmittedCommPreference,
  resetCommPreferenceFlag,
  resetLeadSubmitted,
} = leadFormModal.actions;
